import React, { useState, useMemo } from "react";
import { Dropdown, Form, Button, Card, Badge, Row, Col } from "react-bootstrap";
import { Formik, Form as FormikForm, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";

// 🌟 Mock Static User Data
const mockUsers = [
  { id: "1", name: "Anand Sharma", email: "anand@example.com" },
  { id: "2", name: "Bhavna Patel", email: "bhavna@example.com" },
  { id: "3", name: "Chirag Gupta", email: "chirag@example.com" },
  { id: "4", name: "Deepak Rao", email: "deepak@example.com" },
  { id: "5", name: "Esha Verma", email: "esha@example.com" },
  { id: "6", name: "Farhan Khan", email: "farhan@example.com" },
  { id: "7", name: "Gaurav Singh", email: "gaurav@example.com" },
];

const SendNotification = () => {
  const [userSearch, setUserSearch] = useState("");
  const [isSending, setIsSending] = useState(false);

  // 🌟 Validation Schema
  const NotificationSchema = Yup.object().shape({
    title: Yup.string().required("Notification title is required"),
    selectedUserIds: Yup.array().min(1, "Please select at least one recipient"),
    message: Yup.string()
      .required("Message body cannot be empty")
      .max(500, "Message cannot exceed 500 characters"),
  });

  // 🌟 Filter users inside dropdown based on search input
  const filteredUsers = useMemo(() => {
    return mockUsers.filter(
      (user) =>
        user.name.toLowerCase().includes(userSearch.toLowerCase()) ||
        user.email.toLowerCase().includes(userSearch.toLowerCase())
    );
  }, [userSearch]);

  const handleSubmit = (values, { resetForm }) => {
    setIsSending(true);
    
    // Simulating API action
    setTimeout(() => {
      console.log("Notification Payloads:", values);
      toast.success(`Notification successfully dispatched to ${values.selectedUserIds.length} users!`);
      resetForm();
      setUserSearch("");
      setIsSending(false);
    }, 800);
  };

  return (
    <div className="container mt-4 mb-5" style={{ maxWidth: "800px" }}>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 style={{ color: "#E5484D", fontWeight: "bold" }}>Send Broadcast Notification</h2>
      </div>

      <Card className="shadow-sm border-0 rounded-3">
        <Card.Body className="p-4">
          <Formik
            initialValues={{
              title: "",
              selectedUserIds: [], // Stores selected user IDs strings
              message: "",
            }}
            validationSchema={NotificationSchema}
            onSubmit={handleSubmit}
          >
            {({ values, setFieldValue, errors, touched }) => {
              
              // Helper to toggle a user selection
              const handleUserToggle = (userId) => {
                const currentSelections = [...values.selectedUserIds];
                const index = currentSelections.indexOf(userId);
                
                if (index > -1) {
                  currentSelections.splice(index, 1); // Remove if already checked
                } else {
                  currentSelections.push(userId); // Add if unchecked
                }
                setFieldValue("selectedUserIds", currentSelections);
              };

              // Helper to select all matching users
              const handleSelectAllFiltered = () => {
                const filteredIds = filteredUsers.map(u => u.id);
                // Create a unique set of combined existing selections + filtered matches
                const combined = Array.from(new Set([...values.selectedUserIds, ...filteredIds]));
                setFieldValue("selectedUserIds", combined);
              };

              // Helper to clear all selections
              const handleClearAll = () => {
                setFieldValue("selectedUserIds", []);
              };

              return (
                <FormikForm>
                  {/* 1. Notification Title */}
                  <div className="mb-4">
                    <label className="form-label text-black fw-bold">Notification Title</label>
                    <Field
                      name="title"
                      type="text"
                      placeholder="e.g., System Maintenance Alert"
                      className={`form-control ${errors.title && touched.title ? "is-invalid" : ""}`}
                      style={{ borderRadius: "0.5rem" }}
                    />
                    <ErrorMessage name="title" component="div" className="text-danger small mt-1" />
                  </div>

                  {/* 2. User Selection Dropdown (Searchable + Multi-Select) */}
                  <div className="mb-4">
                    <label className="form-label text-black fw-bold d-block">Recipients (Users)</label>
                    
                    <Dropdown className="w-100">
                      <Dropdown.Toggle 
                        variant="outline-secondary" 
                        className="w-100 text-start d-flex justify-content-between align-items-center"
                        style={{ borderRadius: "0.5rem", borderColor: "black" }}
                      >
                        {values.selectedUserIds.length === 0 
                          ? "Select users..." 
                          : `${values.selectedUserIds.length} User(s) Selected`}
                      </Dropdown.Toggle>

                      {/* Prevent dropdown menu from closing when interacting internally */}
                      <Dropdown.Menu 
                        className="w-100 p-3 shadow" 
                        style={{ maxHeight: "350px", overflowY: "auto" }}
                        onClick={(e) => e.stopPropagation()} 
                      >
                        {/* Search Box Input */}
                        <Form.Control
                          autoFocus
                          type="text"
                          placeholder="Search user by name or email..."
                          className="mb-2"
                          value={userSearch}
                          onChange={(e) => setUserSearch(e.target.value)}
                        />

                        {/* Bulk Action Controls */}
                        <div className="d-flex justify-content-between my-2 pb-2 border-bottom">
                          <Button size="sm" variant="link" className="text-decoration-none p-0 text-success fw-bold" onClick={handleSelectAllFiltered}>
                            + Select All Matched
                          </Button>
                          <Button size="sm" variant="link" className="text-decoration-none p-0 text-danger fw-bold" onClick={handleClearAll}>
                            Clear Selections
                          </Button>
                        </div>

                        {/* Dropdown Options List */}
                        {filteredUsers.length > 0 ? (
                          filteredUsers.map((user) => {
                            const isChecked = values.selectedUserIds.includes(user.id);
                            return (
                              <Form.Check 
                                key={user.id}
                                type="checkbox"
                                id={`user-chk-${user.id}`}
                                label={`${user.name} (${user.email})`}
                                className="py-1 cursor-pointer"
                                checked={isChecked}
                                onChange={() => handleUserToggle(user.id)}
                              />
                            );
                          })
                        ) : (
                          <div className="text-muted text-center my-2 small">No matches found</div>
                        )}
                      </Dropdown.Menu>
                    </Dropdown>
                    <ErrorMessage name="selectedUserIds" component="div" className="text-danger small mt-1" />

                    {/* 🌟 Selected User Chips Display */}
                    {values.selectedUserIds.length > 0 && (
                      <div className="mt-2 p-2 border rounded bg-light d-flex flex-wrap gap-1" style={{ maxHeight: "100px", overflowY: "auto" }}>
                        {values.selectedUserIds.map((id) => {
                          const userObj = mockUsers.find((u) => u.id === id);
                          if (!userObj) return null;
                          return (
                            <Badge 
                              key={id} 
                              bg="secondary" 
                              className="d-flex align-items-center gap-1 p-2 rounded-pill fw-normal"
                              style={{ backgroundColor: "#FFE4E6", color: "#333" }}
                            >
                              {userObj.name}
                              <span 
                                style={{ cursor: "pointer", fontWeight: "bold" }} 
                                className="ms-1 px-1 rounded-circle bg-dark text-white"
                                onClick={() => handleUserToggle(id)}
                              >
                                &times;
                              </span>
                            </Badge>
                          );
                        })}
                      </div>
                    )}
                  </div>

                  {/* 3. Notification Message Content Textarea */}
                  <div className="mb-4">
                    <div className="d-flex justify-content-between">
                      <label className="form-label text-black fw-bold">Message Content</label>
                      <small className="text-muted">{values.message.length}/500 chars</small>
                    </div>
                    <Field
                      name="message"
                      as="textarea"
                      rows={5}
                      placeholder="Type your message details here..."
                      className={`form-control ${errors.message && touched.message ? "is-invalid" : ""}`}
                      style={{ borderRadius: "0.5rem" }}
                    />
                    <ErrorMessage name="message" component="div" className="text-danger small mt-1" />
                  </div>

                  {/* Submit Button Trigger */}
                  <div className="d-grid gap-2">
                    <Button
                      type="submit"
                      disabled={isSending}
                      size="lg"
                      style={{ backgroundColor: "#E5484D", border: "none" }}
                    >
                      {isSending ? "Dispatching Notifications..." : "Send Message"}
                    </Button>
                  </div>
                </FormikForm>
              );
            }}
          </Formik>
        </Card.Body>
      </Card>
    </div>
  );
};

export default SendNotification;